// Persistence layer for Electronic Trade Documents library attachments.
//
// Tracks the mapping between a saved-library document ID and the real
// upload document ID it was attached as in the current in-progress
// shipment. Since the backend allows only one in-progress shipment per
// user, a single localStorage entry is sufficient.
//
// All functions fail silently on storage errors (quota, private mode,
// corrupted data) so the rest of the flow keeps working.

const STORAGE_KEY = 'etd:library-attachments';

export type LibraryAttachmentMap = Record<string, string>;

/**
 * Reads and validates the persisted library-to-document mapping.
 * Returns an empty object on any error or invalid shape.
 */
export function readLibraryAttachments(): LibraryAttachmentMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const result: LibraryAttachmentMap = {};
    for (const [libId, realId] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof libId === 'string' && typeof realId === 'string' && libId && realId) {
        result[libId] = realId;
      }
    }
    return result;
  } catch {
    return {};
  }
}

/** Persists the full mapping. Silent on failure. */
export function writeLibraryAttachments(map: LibraryAttachmentMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // quota exceeded, private mode, etc.
  }
}

/** Adds or updates a single library → real doc mapping. */
export function addLibraryAttachment(libraryId: string, realId: string): void {
  const map = readLibraryAttachments();
  map[libraryId] = realId;
  writeLibraryAttachments(map);
}

/** Removes a mapping by library ID. No-op if absent. */
export function removeLibraryAttachment(libraryId: string): void {
  const map = readLibraryAttachments();
  if (libraryId in map) {
    delete map[libraryId];
    writeLibraryAttachments(map);
  }
}

/**
 * Removes any mapping whose real document ID matches the given ID.
 * Used when the document is deleted via the document row (where we
 * only know the real ID, not the library ID).
 */
export function removeByRealDocumentId(realId: string): void {
  const map = readLibraryAttachments();
  let changed = false;
  for (const [libId, id] of Object.entries(map)) {
    if (id === realId) {
      delete map[libId];
      changed = true;
    }
  }
  if (changed) writeLibraryAttachments(map);
}

/** Clears all persisted library attachments. Called on shipment clear/completion. */
export function clearLibraryAttachments(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
